import { ApolloClient, type DocumentNode } from "@apollo/client";
import { MasterDataGraphQlAdapter } from "@src/infrastructure/adapters/graphQl/MasterDataGraphQlAdapter";
import {
  CREATE_MASTER_DATA_RETAILER,
  GET_MASTER_DATA_RETAILERS,
  UPDATE_MASTER_DATA_RETAILER,
} from "@src/domain/graphql/masterData";
import {
  MasterDataRetailer,
  type IMasterDataRetailer,
} from "@src/domain/entities/masterData";
import type {
  CreateMasterDataRetailerDTO,
  GetMasterDataRetailersDTO,
  UpdateMasterDataRetailerDTO,
} from "@src/domain/dto/masterData";
import type {
  IMasterDataRetailerRepository,
  MasterDataRetailersListResult,
} from "@src/domain/repositories/masterData";
import {
  requireMasterDataMutationData,
  throwIfMasterDataGraphQLError,
  throwIfMasterDataMutationError,
  toMasterDataListResult,
} from "./masterDataRepository.shared";

function mapRetailerItem(item: IMasterDataRetailer): MasterDataRetailer {
  return MasterDataRetailer.from({
    retailerId: item.retailerId,
    retailerKey: item.retailerKey,
    retailerName: item.retailerName,
    matchRulesCount: item.matchRulesCount ?? 0,
    rawAssociationsCount: item.rawAssociationsCount ?? 0,
  });
}

export class MasterDataRetailerRepository implements IMasterDataRetailerRepository {
  private readonly client: ApolloClient;

  constructor(
    client: ApolloClient = MasterDataGraphQlAdapter.getInstance().getClient(),
  ) {
    this.client = client;
  }

  async getRetailers(
    params: GetMasterDataRetailersDTO,
  ): Promise<MasterDataRetailersListResult> {
    const { data, error } = await this.client.query<{
      masterDataRetailers: {
        items: IMasterDataRetailer[];
        total: number;
        page: number;
        limit: number;
      };
    }>({
      query: GET_MASTER_DATA_RETAILERS,
      variables: {
        params: {
          search: params.search ?? null,
          page_number: params.page_number,
          page_size: params.page_size,
        },
      },
      fetchPolicy: "network-only",
      errorPolicy: "all",
    });

    throwIfMasterDataGraphQLError({ error });

    const response = data?.masterDataRetailers;
    if (!response) {
      throw new Error("Failed to fetch master data retailers");
    }

    return toMasterDataListResult(
      response,
      mapRetailerItem,
      params.page_number,
      params.page_size,
    );
  }

  async createRetailer(
    params: CreateMasterDataRetailerDTO,
  ): Promise<MasterDataRetailer> {
    return this.putRetailer(
      CREATE_MASTER_DATA_RETAILER,
      {
        retailerKey: params.retailerKey,
        retailerName: params.retailerName,
      },
      (data) => data?.createMasterDataRetailer,
      "Failed to create retailer",
    );
  }

  async updateRetailer(
    params: UpdateMasterDataRetailerDTO,
  ): Promise<MasterDataRetailer> {
    return this.putRetailer(
      UPDATE_MASTER_DATA_RETAILER,
      {
        retailerId: params.retailerId,
        retailerName: params.retailerName,
      },
      (data) => data?.updateMasterDataRetailer,
      "Failed to update retailer",
    );
  }

  private async putRetailer(
    mutation: DocumentNode,
    mutationParams: Record<string, unknown>,
    pickResponse: (
      data:
        | {
            createMasterDataRetailer?: IMasterDataRetailer;
            updateMasterDataRetailer?: IMasterDataRetailer;
          }
        | null
        | undefined,
    ) => IMasterDataRetailer | null | undefined,
    fallbackMessage: string,
  ): Promise<MasterDataRetailer> {
    const result = await this.client.mutate<{
      createMasterDataRetailer?: IMasterDataRetailer;
      updateMasterDataRetailer?: IMasterDataRetailer;
    }>({
      mutation,
      variables: { params: mutationParams },
      errorPolicy: "all",
    });

    throwIfMasterDataMutationError(result.error);

    return mapRetailerItem(
      requireMasterDataMutationData(
        pickResponse(result.data),
        result.error,
        fallbackMessage,
      ),
    );
  }
}
